import { cellDistance, gridOf } from '../board/geometry';
import { effectiveHitPoints } from '../game/battle';
import { strategicWinProbability as attackerWinProbability } from '../game/combatOdds';
import { summonedElemental } from '../game/elementals';
import { legalMoves } from '../game/game';
import { pieceById } from '../game/movement';
import { activeSpellcaster } from '../game/spells';
import { newPiece } from '../game/piece';
import { canAfford, knowsSpell } from '../game/spellCosts';
import type { Board } from '../board/types';
import type { FactionId, GameContext, GameState, Move, Piece } from '../game/types';
import { pieceWorth, threatsAgainst, type Threat } from './threats';

/**
 * Points per square of distance between a piece and its nearest worthwhile goal. Small next to
 * material (a piece is worth 20–80), so it only decides between otherwise equal moves – but
 * without it the AI never closes in once no favourable capture exists, and games run out on the
 * quiet-turn rule with half the pieces still standing.
 */
const APPROACH_WEIGHT = 0.6;
/** Beyond this many squares a goal is "far" and the exact distance stops mattering. */
const APPROACH_CAP = 8;
/**
 * How much of an advantage a long game eats. A lead is worth less the longer it goes untaken, so
 * the side ahead presses for a result while the side behind is content to let the clock run.
 *
 * Measured against the turn count and nothing else. It used to be measured against the forty-turn
 * quiet clock, which any spell reset — and a multiplier on the score is a multiplier on the worth
 * of resetting it, so at 0.8 casting anything at all was worth five times the same position. The
 * AI duly stopped playing and started casting: 15 of 24 seeds ran to the two-thousand-turn cap
 * with nineteen hundred spells and no fight taken. Tying it to the turn instead keeps the pressure
 * and takes away the purchase, because every move advances the turn by exactly one.
 */
const LONG_GAME_DECAY = 0.8;
/** Turns over which that pressure builds; about one and a half times a normal game's length. */
const LONG_GAME_TURNS = 300;
/**
 * A Summon Elemental still in hand is worth about a pawn. The elemental costs nothing when it
 * dies, so without this the AI spends its free hit on the first goblin it sees; with it, the
 * spell waits for a target worth more than a pawn.
 */
const HELD_SUMMON_VALUE = 14;
/**
 * What a point of magic in hand is worth, against a piece worth 28 to 86.
 *
 * Without this the search reads spell points as free: raising Imprison from six to nine changed
 * how often a mage could cast it and did not move the score it was weighed against by so much as
 * a point. A full mage is worth about a knight in options, which is roughly right — magic is
 * worth having and not worth more than the board.
 */
const SPELL_POINT_VALUE = 2;

export interface AiOptions {
  /** Diagnostic only; fitted to Expert Arena, never enabled by production callers. */
  survivorModel?: 'conditional-expert';
  /** Diagnostic-only full-health material values. Omitted in production. */
  materialValues?: Readonly<Record<string, number>>;
  /** 0 = careless … 1 = careful; scales how much threats are feared. */
  caution?: number;
  /**
   * Judge a position from the side to move. After the enemy's move it is my turn, so the fights
   * I could start are opportunities and the enemy's are a move away; without this every leaf an
   * even number of plies deep counts the enemy's threats as if it moved twice, and a four-ply
   * search retreats from positions a three-ply search would hold.
   */
  initiative?: boolean;
  /**
   * The balance bar's reading rather than the search's (`advantage`): the other sides count as one
   * average opponent, not their sum, so three to six sides can each read even; and a lead keeps its
   * worth however long the game runs.
   */
  balanceBar?: boolean;
}

/**
 * Legal moves worth evaluating. Teleport, Exchange and Summon Elemental have hundreds of targets,
 * most of them pointless, and they are cast once a game: only the few most promising of each are
 * kept – teleports onto a power point or next to an enemy the piece would beat, exchanges that
 * drag a strong enemy piece towards our side, and summons against the enemies worth the most
 * that the elemental would most likely beat (one per enemy: the square the elemental appears
 * on changes nothing after the fight, so the one nearest the mage stands for them all). Every
 * other spell with a target is capped the same way: holds on the enemies worth the most, heals
 * on the most wounded, revivals of the highest tier and releases of the pieces worth the most.
 */
export function candidateMoves(ctx: GameContext, state: GameState, spellCap = 8): Move[] {
  const me = state.activeFaction;
  const tier = (character: string) => ctx.roster[character]?.tier ?? 1;
  const powerPoints = new Set(ctx.board.def.powerPoints.map(([x, y]) => `${x},${y}`));
  const enemies = state.pieces.filter((p) => p.faction !== me);
  const teleports: { move: Move; key: number }[] = [];
  const exchanges: { move: Move; key: number }[] = [];
  const targeted = new Map<string, { move: Move; key: number }[]>();
  const keep = (family: string, move: Move, key: number) => {
    const list = targeted.get(family);
    if (list) list.push({ move, key });
    else targeted.set(family, [{ move, key }]);
  };
  const summons = new Map<number, { move: Move; key: number; distance: number }>();
  // A threatened piece carried out of every enemy's reach is a rescue, the signature Teleport of
  // the game, which the list used to leave out (core-ai:bugs#11). What each of my pieces stands to
  // lose is worked out once, and only if a Teleport is on offer.
  let danger: Map<number, number> | undefined;
  const dangerOf = (piece: Piece) => {
    if (!danger) {
      danger = new Map();
      for (const t of threatsAgainst(ctx, state, me))
        danger.set(t.target.id, Math.max(danger.get(t.target.id) ?? 0, t.gain));
    }
    return danger.get(piece.id);
  };
  const outOfReach = (at: { x: number; y: number }) =>
    enemies.every(
      (e) =>
        e.imprisoned ||
        e.frozen > 0 ||
        cellDistance(ctx.board, e, at) > (ctx.roster[e.character]?.movement.range ?? 1),
    );
  const escapes = new Map<number, { move: Move; key: number }>();
  const others: Move[] = [];
  const mage = activeSpellcaster(ctx, state);
  const elemental = summonedElemental(ctx, state);
  // Expected worth of the kill: the elemental costs nothing but the spell, so only the upside counts.
  const summonKey = (target: Piece): number => {
    if (!elemental) return 0;
    const probe: GameState = {
      ...state,
      phase: 'battle',
      pieces: [...state.pieces, newPiece(ctx.roster, -1, me, elemental.id, 0, 0)],
      pendingBattle: { attackerId: -1, defenderId: target.id, x: target.x, y: target.y },
    };
    const p = attackerWinProbability(ctx, probe);
    return p * (tier(target.character) * 10 + effectiveHitPoints(ctx, state, target));
  };
  for (const m of legalMoves(ctx, state)) {
    if (m.type !== 'spell') {
      others.push(m);
      continue;
    }
    if (m.spell === 'summon-elemental') {
      const target = pieceById(state, m.pieceId);
      if (!target) continue;
      const distance = mage ? cellDistance(ctx.board, mage, m) : 0;
      const known = summons.get(target.id);
      if (known && known.distance <= distance) continue;
      summons.set(target.id, { move: m, key: known?.key ?? summonKey(target), distance });
    } else if (m.spell === 'teleport') {
      const piece = pieceById(state, m.pieceId);
      if (!piece) continue;
      let key = powerPoints.has(`${m.x},${m.y}`) ? 10 : 0;
      for (const e of enemies) {
        if (cellDistance(ctx.board, e, m) !== 1) continue;
        key += Math.max(0, tier(piece.character) - tier(e.character) + 1) * 2;
      }
      if (key > 0) teleports.push({ move: m, key: key + tier(piece.character) });
      else {
        // Out of harm's way: one square per threatened piece, the nearest that no enemy reaches.
        const stake = dangerOf(piece);
        if (stake !== undefined && outOfReach(m)) {
          const escape = stake - cellDistance(ctx.board, piece, m) * 0.01;
          const known = escapes.get(piece.id);
          if (!known || escape > known.key) escapes.set(piece.id, { move: m, key: escape });
        }
      }
    } else if (m.spell === 'exchange') {
      const a = pieceById(state, m.pieceId);
      const b = pieceById(state, m.otherId);
      if (!a || !b || (a.faction === me) === (b.faction === me)) continue;
      const mine = a.faction === me ? a : b;
      const theirs = a.faction === me ? b : a;
      exchanges.push({ move: m, key: tier(theirs.character) - tier(mine.character) });
    } else if (
      m.spell === 'imprison' ||
      m.spell === 'curse' ||
      m.spell === 'freeze' ||
      m.spell === 'release'
    ) {
      const target = pieceById(state, m.pieceId);
      if (target) keep(m.spell, m, pieceWorth(ctx, state, target));
    } else if (m.spell === 'heal') {
      const target = pieceById(state, m.pieceId);
      if (target) keep(m.spell, m, target.damage);
    } else if (m.spell === 'revive') keep(m.spell, m, tier(m.character));
    else others.push(m);
  }
  const top = (list: { move: Move; key: number }[]) =>
    list
      .sort((x, y) => y.key - x.key)
      .slice(0, spellCap)
      .map((x) => x.move);
  return [
    ...others,
    ...top([...teleports, ...escapes.values()]),
    ...top(exchanges),
    ...top([...summons.values()]),
    ...[...targeted.values()].flatMap(top),
  ];
}

/** Material, power points, square favourability and exposure to attack, from `me`'s perspective. */
export function evaluate(
  ctx: GameContext,
  state: GameState,
  me: FactionId,
  options: AiOptions = {},
): number {
  if (state.phase === 'ended') {
    if (state.winner === me) return 10_000;
    if (state.winner === 'draw') return 0;
    return -10_000;
  }
  const caution = options.caution ?? 0.7;
  // Each other side weighs 1 in the search's paranoid sum, and a share of one opponent on the bar.
  const others = options.balanceBar
    ? 1 / Math.max(1, state.factions.filter((f) => f.id !== me && !f.eliminated).length)
    : 1;
  let score = 0;
  for (const piece of state.pieces) {
    const character = ctx.roster[piece.character];
    if (!character) continue;
    const sign = piece.faction === me ? 1 : -others;
    const value = pieceWorth(ctx, state, piece, options.materialValues);
    score += sign * value;
    if (ctx.board.cells[piece.y * ctx.board.width + piece.x]?.powerPoint) score += sign * 12;
    if (piece.imprisoned) score -= sign * 8;
    // Held magic is an option not yet spent, so spending it has to cost the search something.
    if (piece.spellPoints > 0) score += sign * piece.spellPoints * SPELL_POINT_VALUE;
  }
  // Pressure: every side wants its pieces near enemies it can beat and near unheld power points.
  score +=
    APPROACH_WEIGHT *
    (approach(ctx, state, (p) => p.faction !== me) -
      others * approach(ctx, state, (p) => p.faction === me));

  // Exposure: enemy pieces that could attack one of mine next turn and would gain by it
  // (`threatsAgainst`). Fearing every possible attack, not just the profitable ones, made both
  // sides back away from even match-ups and no game ever ended.
  if (options.initiative && state.activeFaction === me && state.phase === 'play') {
    // My move: the profitable fights I can start are worth what the enemy's would have cost.
    for (const faction of state.factions)
      if (faction.id !== me && !faction.eliminated)
        for (const threat of threatsAgainst(ctx, state, faction.id, options.materialValues, me))
          score += caution * threat.gain * 0.5;
    // But one move saves one piece: of a fork, all but the piece most at stake stays threatened.
    // The leaf counted none of it, so the levels that judge a leaf from the side to move walked
    // into forks the others saw (core-ai:bugs#12).
    score -= caution * unparried(threatsAgainst(ctx, state, me, options.materialValues)) * 0.5;
  } else
    for (const threat of threatsAgainst(ctx, state, me, options.materialValues))
      score -= caution * threat.gain * 0.5;
  // A summon still in hand, when the roster has elementals to bring.
  if (Object.values(ctx.roster).some((c) => c.summoned))
    for (const f of state.factions) {
      // A summon in hand is no longer a side's to hold: it is a caster who knows it and can pay.
      if (f.eliminated) continue;
      const holds = state.pieces.some(
        (p) =>
          p.faction === f.id &&
          !p.imprisoned &&
          knowsSpell(ctx.roster[p.character], 'summon-elemental') &&
          canAfford(p, 'summon-elemental'),
      );
      if (!holds) continue;
      score += (f.id === me ? 1 : -others) * HELD_SUMMON_VALUE;
    }
  if (options.balanceBar) return score;
  const dragging = Math.min(1, state.turn / LONG_GAME_TURNS);
  return score * (1 - LONG_GAME_DECAY * dragging);
}

/**
 * What a fork still threatens once its victim has moved: the gain at stake on every threatened
 * piece but the one most at stake. A piece counts its worst attacker only, since moving it away
 * saves it from them all.
 */
export function unparried(threats: readonly Threat[]): number {
  const worst = new Map<number, number>();
  for (const t of threats) worst.set(t.target.id, Math.max(worst.get(t.target.id) ?? 0, t.gain));
  if (worst.size < 2) return 0;
  let total = 0;
  let most = 0;
  for (const gain of worst.values()) {
    total += gain;
    most = Math.max(most, gain);
  }
  return total - most;
}

/**
 * How far a side's free pieces stand from something worth reaching: the nearest enemy of no
 * higher tier (or any enemy when none is that weak) or a power point the side does not hold.
 * On squares fliers and teleporters cross diagonally and ground pieces walk the grid; on hexes
 * both count the hexes between.
 */
function approach(ctx: GameContext, state: GameState, isEnemy: (piece: Piece) => boolean): number {
  const mine: Piece[] = [];
  const enemies: Piece[] = [];
  for (const piece of state.pieces) {
    if (isEnemy(piece)) enemies.push(piece);
    else if (!piece.imprisoned) mine.push(piece);
  }
  if (mine.length === 0 || enemies.length === 0) return 0;
  const goals = ctx.board.def.powerPoints.filter(
    ([x, y]) => !mine.some((piece) => piece.x === x && piece.y === y),
  );
  // The search judges thousands of positions a move: the enemies' squares and tiers are read once.
  const xs = enemies.map((enemy) => enemy.x);
  const ys = enemies.map((enemy) => enemy.y);
  const tiers = enemies.map((enemy) => ctx.roster[enemy.character]?.tier ?? 1);
  const hex = gridOf(ctx.board) === 'hex';
  let total = 0;
  for (const piece of mine) {
    const character = ctx.roster[piece.character];
    if (!character) continue;
    const ground = character.movement.type === 'ground';
    let weaker = APPROACH_CAP;
    let stronger = APPROACH_CAP;
    let weakerSeen = false;
    for (let i = 0; i < xs.length; i++) {
      const distance = stepsTo(ctx.board, hex, ground, piece, xs[i]!, ys[i]!);
      if (tiers[i]! <= character.tier) {
        weakerSeen = true;
        weaker = Math.min(weaker, distance);
      } else stronger = Math.min(stronger, distance);
    }
    let nearest = weakerSeen ? weaker : stronger;
    for (const [x, y] of goals)
      nearest = Math.min(nearest, stepsTo(ctx.board, hex, ground, piece, x, y));
    total += nearest;
  }
  return total;
}

/** The squares between a piece and (x, y), walked or flown; on hexes, the hexes between. */
function stepsTo(
  board: Board,
  hex: boolean,
  ground: boolean,
  piece: Piece,
  x: number,
  y: number,
): number {
  if (hex) return cellDistance(board, piece, { x, y });
  const dx = Math.abs(x - piece.x);
  const dy = Math.abs(y - piece.y);
  return ground ? dx + dy : Math.max(dx, dy);
}
